import React, { useCallback } from 'react';
import { Printer } from 'lucide-react';

import {
    getCompanySourceBadgeLabel,
    getCompanySourceColors,
} from 'components/receiving/components/AllocatedReceiving/AllocationBreakdown/companySourceAllocationConfig';
import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import dayjs from 'utils/dayjsConfig';

import { isValidCaseLabelCount, sanitizeCaseLabelCountInput } from './printLabelsUtils';

function formatBbd(expiresOn) {
    if (!expiresOn) {
        return 'BBD N/A';
    }

    const formatted = dayjs(expiresOn).format('MM/DD/YYYY');
    return formatted === 'Invalid Date' ? 'BBD N/A' : `BBD ${formatted}`;
}

function renderSourceLabel({ badgeColor, badgeLabel, isThirdPartyAsn, lot, partnerDisplayName }) {
    if (isThirdPartyAsn) {
        return <span className="text-sm font-semibold">{partnerDisplayName ?? 'Partner'}</span>;
    }

    if (badgeLabel) {
        return (
            <span
                className="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-semibold text-white"
                style={{ backgroundColor: badgeColor }}
            >
                {badgeLabel}
            </span>
        );
    }

    if (lot.companySource) {
        return <span className="text-sm font-semibold">{lot.companySource.displayName}</span>;
    }

    return null;
}

const NOOP = () => {};

/**
 * @param {Object} props
 * @param {string} props.fieldIdPrefix
 * @param {boolean} [props.isThirdPartyAsn]
 * @param {boolean} [props.isPrinting]
 * @param {{ id: string, caseCount: number, expiresOn: string | null, companySource: { id: number, code: string, displayName: string } | null }} props.lot
 * @param {number} props.lotIndex
 * @param {string | null} [props.partnerDisplayName]
 * @param {number | ''} props.caseLabelCount
 * @param {(lotId: string, qty: number | '') => void} [props.onCaseLabelCountChange]
 * @param {(lotId: string, qty: number) => void} [props.onPrintCaseTags]
 */
function PrintPalletLotRow({
    caseLabelCount,
    fieldIdPrefix,
    isPrinting = false,
    isThirdPartyAsn = false,
    lot,
    lotIndex,
    onCaseLabelCountChange = NOOP,
    onPrintCaseTags = NOOP,
    partnerDisplayName = null,
}) {
    const caseLabelsInputId = `${fieldIdPrefix}-case-labels`;
    const casesLabel = lot.caseCount === 1 ? 'case' : 'cases';
    const badgeLabel = lot.companySource ? getCompanySourceBadgeLabel(lot.companySource.code) : null;
    const { badgeColor } = lot.companySource ? getCompanySourceColors(lot.companySource.code) : { badgeColor: null };
    const lotIdLabel = lot.id ? `Lot ${lot.id}` : null;

    const handleCaseLabelCountChange = useCallback(
        (event) => {
            const sanitized = sanitizeCaseLabelCountInput(event.target.value, lot.caseCount);
            onCaseLabelCountChange(lot.id, sanitized);
        },
        [lot.caseCount, lot.id, onCaseLabelCountChange],
    );

    const canPrint = isValidCaseLabelCount(caseLabelCount, lot.caseCount);

    return (
        <div className="flex items-center justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
                <span className="text-xs text-muted-foreground">Lot {lotIndex + 1}</span>
                {renderSourceLabel({ badgeColor, badgeLabel, isThirdPartyAsn, lot, partnerDisplayName })}
                <span className="text-sm font-semibold">
                    {lot.caseCount} {casesLabel}
                </span>
                <span className="text-xs text-muted-foreground">{formatBbd(lot.expiresOn)}</span>
                <span className="text-xs text-muted-foreground">{lotIdLabel}</span>
            </div>
            <div className="flex shrink-0 items-end gap-2">
                <div className="flex flex-col gap-1">
                    <Label className="text-xs leading-none text-muted-foreground" htmlFor={caseLabelsInputId}>
                        Case labels
                    </Label>
                    <Input
                        id={caseLabelsInputId}
                        className="h-8 w-20 text-sm"
                        min={1}
                        step={1}
                        type="number"
                        value={caseLabelCount}
                        onChange={handleCaseLabelCountChange}
                    />
                </div>
                <Button
                    className="h-8 shrink-0"
                    disabled={isPrinting || !canPrint}
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => onPrintCaseTags(lot.id, caseLabelCount)}
                >
                    <Printer aria-hidden className="size-4" />
                    Print Case Tags
                </Button>
            </div>
        </div>
    );
}

export { formatBbd, NOOP, PrintPalletLotRow };
